import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../../services/api';
import { useSnackbar } from '../../../components/common/Snackbar';
import { DEFAULT_QUERY_TYPE_OPTIONS } from '../../../constants/raiseQueryPage';
import { formatQueryType } from '../../../utils/queryListUtils';
import {
  buildProjectFilterOptionsFromApi,
  formatQueryProjectNameForDisplay,
} from '../../../utils/projectsUtils';
import { formatPostShortName } from '../../../services/postSelection';

/**
 * Hook to manage query data fetching and processing for RaiseQuery page
 * @param {Object} params - Hook parameters
 * @param {string} params.searchQuery - Search query string
 * @param {Object} params.filters - Filter object
 * @param {string} params.currentUserRole - Current user role
 * @param {string} params.currentUserRegion - Current user region
 * @param {Function} params.setCurrentUserRole - Setter for user role
 * @param {Function} params.setCurrentUserRegion - Setter for user region
 * @param {Function} params.setAllQueries - Setter for all queries
 * @param {Function} params.setLoading - Setter for loading state
 * @param {Function} params.setProjectOptions - Setter for project options
 * @param {Function} params.setAssignedFilterOptions - Setter for assigned filter options
 * @param {Function} params.setQueryTypeOptions - Setter for query type options
 * @param {Function} params.setStatusSummary - Setter for status summary
 * @param {Function} params.setEdUsersByRegion - Setter for ED users by region
 * @returns {Function} Fetch queries function
 */
export const useRaiseQueryPageData = ({
  searchQuery,
  filters,
  currentUserRole,
  currentUserRegion,
  setCurrentUserRole,
  setCurrentUserRegion,
  setAllQueries,
  setLoading,
  setProjectOptions,
  setAssignedFilterOptions,
  setQueryTypeOptions,
  setStatusSummary,
  setBackendCounts,
  setEdUsersByRegion,
}) => {
  const { showSnackbar } = useSnackbar();
  const baseQueriesRef = useRef([]);
  const hasBaseQueriesRef = useRef(false);
  const projectsCatalogRef = useRef([]);
  const [projectsCatalog, setProjectsCatalog] = useState([]);

  // Fetch projects for filters + catalog for "name - description" headers
  useEffect(() => {
    const fetchProjectsForFilters = async () => {
      try {
        const response = await api.get('/projects', {
          params: {
            limit: 1000,
          },
        });
        const projectsData =
          response.data?.data?.projects ||
          response.data?.projects ||
          [];
        const list = Array.isArray(projectsData) ? projectsData : [];
        projectsCatalogRef.current = list;
        setProjectsCatalog(list);
        setProjectOptions(buildProjectFilterOptionsFromApi(list));
      } catch (error) {
        // console.error('Failed to load projects for filters:', error);
      }
    };

    fetchProjectsForFilters();
  }, [setProjectOptions]);

  // Fetch ED users by region
  useEffect(() => {
    const fetchEdUsers = async () => {
      try {
        const response = await api.get('/auth/ed-users-by-region');
        if (response.data?.success && response.data?.data?.edByRegion) {
          setEdUsersByRegion(response.data.data.edByRegion);
        }
      } catch (error) {
        // console.error('Failed to load ED users:', error);
        // Don't show error to user, just log it
      }
    };

    fetchEdUsers();
  }, [setEdUsersByRegion]);

  const fetchQueries = useCallback(async () => {
    setLoading(true);
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        showSnackbar('User not found. Please login again.', 'error');
        setLoading(false);
        return;
      }

      const parsedUser = JSON.parse(userStr);
      const currentUserId = parsedUser.id || parsedUser._id;
      const role = parsedUser.role || '';
      const region = parsedUser.region || '';
      const isAdmin = role.toLowerCase() === 'admin';
      setCurrentUserRole((prev) => (prev === role ? prev : role));
      setCurrentUserRegion((prev) => (prev === region ? prev : region));

      // Fetch a larger set to enable region filtering, then paginate client-side
      const params = {
        page: 1,
        limit: 1000, // Fetch more to enable region filtering
        sortBy: 'raisedAt',
        sortOrder: 'desc',
        userId: currentUserId,
        userRole: role,
      };

      // NOTE: Do NOT send search to the API.
      // We keep a base list fetched from the server, and apply search locally so clearing the search
      // instantly restores the full results without a second network call.

      // Add filters
      if (filters.status) params.status = filters.status;
      if (filters.projectName) params.projectName = filters.projectName;
      if (filters.queryType) params.queryType = filters.queryType;
      if (!isAdmin && currentUserId) {
        params.raisedBy = currentUserId;
      } else if (isAdmin) {
        params.adminView = true;
      }

      const response = await api.get('/queries', { params });

      // ── Backend counts (single source of truth for stat cards) ──────────────
      const myQueriesCounts = response.data?.counts?.myQueries || null;
      if (setBackendCounts) setBackendCounts(myQueriesCounts);
      // ────────────────────────────────────────────────────────────────────────

      let fetched = response.data.data.queries || [];
// console.log('fetched', fetched);
      if (!isAdmin && currentUserId) {
        fetched = fetched.filter((query) => {
          const raisedById =
            query.raisedBy?._id ||
            query.raisedBy?.id ||
            (typeof query.raisedBy === 'string' ? query.raisedBy : null);
          return raisedById && raisedById.toString() === currentUserId.toString();
        });
      }

      // Store base list for client-side search updates
      baseQueriesRef.current = Array.isArray(fetched) ? fetched : [];
      hasBaseQueriesRef.current = true;

      // Grouped by post (query.postShortName — the assignee's post at raise
      // time, see Query model) rather than by assignee id, so selecting an
      // option filters to every query tagged with that post, regardless of
      // who currently holds it. Queries without a postShortName (legacy, or
      // never post-scoped) fall back to grouping by assignee id instead, so
      // they're still filterable individually.
      const postOptionsMap = new Map();
      let includeUnassigned = false;
      fetched.forEach((query) => {
        const assignee = query?.assignee;
        if (!assignee || !(assignee._id || assignee.id)) {
          includeUnassigned = true;
          return;
        }

        const name =
          assignee.name ||
          assignee.fullName ||
          assignee.username ||
          'Unnamed';
        const postBase = query?.postShortName
          ? formatPostShortName(query.postShortName)
          : '';

        const value = postBase
          ? `post:${postBase.toLowerCase()}`
          : `assignee:${assignee._id || assignee.id}`;
        const label = postBase ? `${name} (${postBase})` : name;

        if (!postOptionsMap.has(value)) {
          postOptionsMap.set(value, label);
        }
      });
      const assigneeOptions = [];
      if (includeUnassigned) {
        assigneeOptions.push({ value: 'unassigned', label: 'Unassigned' });
      }
      const sortedAssignees = Array.from(postOptionsMap.entries())
        .sort((a, b) =>
          (a[1] || '').localeCompare(b[1] || '', undefined, {
            sensitivity: 'base',
          })
        )
        .map(([value, label]) => ({ value, label }));
      assigneeOptions.push(...sortedAssignees);
      setAssignedFilterOptions(assigneeOptions);

      const dynamicQueryTypes = new Set(
        fetched
          .map((query) => query?.queryType)
          .filter((type) => typeof type === 'string' && type.trim() !== '')
      );
      const mergedQueryTypeOptions = [...DEFAULT_QUERY_TYPE_OPTIONS];
      dynamicQueryTypes.forEach((type) => {
        const formattedLabel = formatQueryType(type);
        const exists = mergedQueryTypeOptions.some(
          (option) => option.label.toLowerCase() === formattedLabel.toLowerCase()
        );
        if (!exists) {
          mergedQueryTypeOptions.push({
            value: formattedLabel,
            label: formattedLabel,
          });
        }
      });
      mergedQueryTypeOptions.sort((a, b) =>
        (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' })
      );
      setQueryTypeOptions(mergedQueryTypeOptions);

      const trimmedSearch = (searchQuery || '').trim().toLowerCase();
      let processedQueries = [...fetched];
      if (trimmedSearch) {
        processedQueries = processedQueries.filter((q) => {
          const id = (q.queryId || q._id || '').toString().toLowerCase();
          const header = (q.queryHeader || '').toLowerCase();
          const desc = (q.queryDescription || '').toLowerCase();
          const project = (q.projectName || '').toLowerCase();
          return (
            id.includes(trimmedSearch) ||
            header.includes(trimmedSearch) ||
            desc.includes(trimmedSearch) ||
            project.includes(trimmedSearch)
          );
        });
      }

      if (filters.status) {
        processedQueries = processedQueries.filter(
          (query) => (query.status || '').toLowerCase() === filters.status.toLowerCase()
        );
      }

      if (filters.projectName) {
        processedQueries = processedQueries.filter(
          (query) =>
            (query.projectName || '').toString().toLowerCase() ===
            filters.projectName.toString().toLowerCase()
        );
      }

      if (filters.queryType) {
        processedQueries = processedQueries.filter(
          (query) =>
            (query.queryType || '').toString().toLowerCase() ===
            filters.queryType.toString().toLowerCase()
        );
      }

      if (filters.assignedTo) {
        if (filters.assignedTo === 'unassigned') {
          processedQueries = processedQueries.filter(
            (query) => !query.assignee || !(query.assignee._id || query.assignee.id)
          );
        } else {
          processedQueries = processedQueries.filter((query) => {
            const assigneeId =
              query.assignee?._id ||
              query.assignee?.id ||
              (typeof query.assignee === 'string' ? query.assignee : null);
            return (
              assigneeId &&
              assigneeId.toString().toLowerCase() === filters.assignedTo.toString().toLowerCase()
            );
          });
        }
      }

      setAllQueries(processedQueries);

      // Status summary is now driven by backend counts via buildRegionStatusSummary.
      // The setStatusSummary call is intentionally omitted here.

      const catalog = projectsCatalogRef.current || [];
      const projectNameMap = new Map();
      processedQueries.forEach((query) => {
        const projectName = (query.projectName || '').toString().trim();
        if (!projectName) return;
        const key = projectName.toLowerCase();
        if (!projectNameMap.has(key)) {
          projectNameMap.set(key, {
            value: projectName,
            label: formatQueryProjectNameForDisplay(projectName, catalog),
          });
        }
      });
      setProjectOptions((prev) => {
        const merged = new Map();
        (Array.isArray(prev) ? prev : []).forEach((option) => {
          if (!option) return;
          const value = (option.value || option.label || '').toString().trim();
          if (!value) return;
          const key = value.toLowerCase();
          if (!merged.has(key)) {
            merged.set(key, {
              value: option.value || option.label,
              label: option.label || option.value,
            });
          }
        });
        projectNameMap.forEach((option, key) => {
          if (!merged.has(key)) {
            merged.set(key, option);
          }
        });
        return Array.from(merged.values()).sort((a, b) =>
          (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' })
        );
      });
    } catch (error) {
      // console.error('Error fetching queries:', error);
      showSnackbar('Failed to load queries', 'error');
    } finally {
      setLoading(false);
    }
  }, [
    searchQuery,
    filters,
    setCurrentUserRole,
    setCurrentUserRegion,
    setAllQueries,
    setLoading,
    setProjectOptions,
    setAssignedFilterOptions,
    setQueryTypeOptions,
    setStatusSummary,
    setBackendCounts,
    showSnackbar,
  ]);

  // Fetch base queries when filters change (search is handled client-side)
  useEffect(() => {
    fetchQueries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Apply search instantly on the base list (no API call)
  useEffect(() => {
    if (!hasBaseQueriesRef.current) return;
    const fetched = baseQueriesRef.current || [];

    const trimmedSearch = (searchQuery || '').trim().toLowerCase();
    let processedQueries = [...fetched];

    if (trimmedSearch) {
      processedQueries = processedQueries.filter((q) => {
        const id = (q.queryId || q._id || '').toString().toLowerCase();
        const header = (q.queryHeader || '').toLowerCase();
        const desc = (q.queryDescription || '').toLowerCase();
        const project = (q.projectName || '').toLowerCase();
        return (
          id.includes(trimmedSearch) ||
          header.includes(trimmedSearch) ||
          desc.includes(trimmedSearch) ||
          project.includes(trimmedSearch)
        );
      });
    }

    // Apply current filters as well, so behavior matches the fetch-time pipeline
    if (filters.status) {
      processedQueries = processedQueries.filter(
        (query) => (query.status || '').toLowerCase() === filters.status.toLowerCase()
      );
    }
    if (filters.projectName) {
      processedQueries = processedQueries.filter(
        (query) =>
          (query.projectName || '').toString().toLowerCase() ===
          filters.projectName.toString().toLowerCase()
      );
    }
    if (filters.queryType) {
      processedQueries = processedQueries.filter(
        (query) =>
          (query.queryType || '').toString().toLowerCase() ===
          filters.queryType.toString().toLowerCase()
      );
    }
    if (filters.assignedTo) {
      if (filters.assignedTo === 'unassigned') {
        processedQueries = processedQueries.filter(
          (query) => !query.assignee || !(query.assignee._id || query.assignee.id)
        );
      } else {
        processedQueries = processedQueries.filter((query) => {
          const assigneeId =
            query.assignee?._id ||
            query.assignee?.id ||
            (typeof query.assignee === 'string' ? query.assignee : null);
          return (
            assigneeId &&
            assigneeId.toString().toLowerCase() === filters.assignedTo.toString().toLowerCase()
          );
        });
      }
    }

    setAllQueries(processedQueries);
    // Status summary is driven by backend counts – do not override from UI list.
  }, [searchQuery, filters, setAllQueries, setStatusSummary]);

  return {
    fetchQueries,
    projectsCatalog,
  };
};
